// MilestoneFilterChipBar — adapts the deliverable FilterChipBar to the
// milestone surface. Chips: Status (multi-select), SOW (multi-select,
// wide popover with its own SOW search above SOW_SEARCH_THRESHOLD
// options), Awaiting acceptance (binary), Include archived (binary).
// Plus a full-width search input scoped to milestone names.
//
// Clear-all lives on the MilestoneActiveFilterSummary strip above the
// chip bar — the chip-bar itself does not render its own Clear-all
// affordance, matching the deliverable surface where the active-filter
// summary owns the reset action.
//
// Visual structure mirrors FilterChipBar.tsx exactly so the two tabs
// read alike. Search debounce + URL round-trip are owned by the parent
// (MilestoneTab); this component is a controlled-input shell.

import { useEffect, useMemo, useRef, useState } from "react";
import type { Sow } from "../../../../types";
import type { MilestoneDerivedStatus } from "../../../../utils/deriveMilestoneStatus";
import {
  UNATTACHED_TOKEN,
  type MilestoneFilterChipBarFilters,
} from "./milestoneUrlState";
import DebouncedSearchInput from "../shared/DebouncedSearchInput";
import {
  SOW_SEARCH_THRESHOLD,
  matchesSowSearch,
  compareSowNumbers,
} from "../shared/sowPickerSearch";

export interface MilestoneFilterChipBarProps {
  filters: MilestoneFilterChipBarFilters;
  onFiltersChange: (next: MilestoneFilterChipBarFilters) => void;
  search: string;
  onSearchChange: (next: string) => void;
  /** SOWs on the parent contract — drives the SOW chip popover.
   *  Archived/cancelled excluded from options (consistent with the
   *  form picker). */
  sows?: readonly Sow[];
  /** Hide the SOW chip when the surface is already SOW-scoped via the
   *  scope selector. Defaults to true. */
  showSowChip?: boolean;
  /** Bump to force the local search-input state to resync with the
   *  `search` prop AND drop any in-flight debounce. Needed because the
   *  user can hit Clear-all (on MilestoneActiveFilterSummary, above
   *  this bar) *before* the typing-debounce flushes — at which point
   *  `search` is still the (empty) committed prop value, so a
   *  `[search]`-only resync won't fire. The parent increments this
   *  counter on every Clear-all to force the reset regardless of
   *  whether the prop value visibly changed. */
  resetCounter?: number;
}

interface OptionDef<V extends string> {
  value: V;
  label: string;
}

const STATUS_OPTIONS: OptionDef<MilestoneDerivedStatus>[] = [
  { value: "upcoming", label: "Upcoming" },
  { value: "delivered", label: "Delivered" },
  { value: "partially-accepted", label: "Partially accepted" },
  { value: "accepted", label: "Accepted" },
  { value: "paid", label: "Paid" },
];

const CHIP_BASE =
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-caption font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400";

const CHIP_INACTIVE =
  "border-slate-300 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700";

const CHIP_ACTIVE =
  "border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-500 dark:bg-brand-950 dark:text-brand-300";

const POPOVER_CLASS =
  "absolute z-20 mt-1 min-w-[180px] max-h-[60vh] overflow-y-auto rounded-md border border-slate-200 bg-white p-2 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800";

// SOW popover: fixed comfortable width instead of shrink-to-fit — prod
// SOW names run long (road-project descriptions) and wrap into an
// unreadable sliver at the default min width. Viewport-capped so it
// never overflows small screens.
const SOW_POPOVER_CLASS =
  "absolute z-20 mt-1 w-96 max-w-[calc(100vw-2rem)] max-h-[60vh] overflow-y-auto rounded-md border border-slate-200 bg-white p-2 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800";

interface StatusChipProps {
  selected: MilestoneDerivedStatus[];
  onToggle: (next: MilestoneDerivedStatus[]) => void;
}

function StatusChip({ selected, onToggle }: StatusChipProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isActive = selected.length > 0;

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  const handleToggleOption = (value: MilestoneDerivedStatus) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onToggle(next);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={
          isActive
            ? `Status (${selected.length} selected)`
            : "Status"
        }
        className={`${CHIP_BASE} ${isActive ? CHIP_ACTIVE : CHIP_INACTIVE}`}
      >
        <span>Status</span>
        {isActive && (
          <span className="rounded bg-white px-1 text-[10px] text-brand-600 dark:bg-brand-900 dark:text-brand-300">
            {selected.length}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="filter-popover-status"
          role="listbox"
          aria-multiselectable="true"
          className={POPOVER_CLASS}
        >
          {/* Bulk toggle so "everything except X" is two clicks: Select
              all, then untick X. Flips to Clear all once every status is
              selected. */}
          <button
            type="button"
            data-testid="filter-status-select-all"
            onClick={() =>
              onToggle(
                selected.length === STATUS_OPTIONS.length
                  ? []
                  : STATUS_OPTIONS.map((o) => o.value),
              )
            }
            className="mb-1 w-full rounded px-2 py-1 text-left text-caption font-medium text-brand-600 hover:bg-slate-50 dark:text-brand-400 dark:hover:bg-slate-700"
          >
            {selected.length === STATUS_OPTIONS.length
              ? "Clear all"
              : "Select all"}
          </button>
          <ul className="space-y-1">
            {STATUS_OPTIONS.map((opt) => {
              const id = `filter-status-${opt.value}`;
              return (
                <li key={opt.value}>
                  <label
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-body-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <input
                      id={id}
                      type="checkbox"
                      checked={selected.includes(opt.value)}
                      onChange={() => handleToggleOption(opt.value)}
                      className="accent-brand-600"
                    />
                    <span>{opt.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

interface SowChipProps {
  options: OptionDef<string>[];
  selected: string[];
  onToggle: (next: string[]) => void;
}

function SowChip({ options, selected, onToggle }: SowChipProps) {
  const [open, setOpen] = useState(false);
  const [sowSearch, setSowSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const isActive = selected.length > 0;

  // Unattached is a mode, not a SOW — it stays pinned above the list and
  // is never filtered out by the search.
  const pinnedOptions = options.filter((o) => o.value === UNATTACHED_TOKEN);
  const sowOnlyOptions = options.filter((o) => o.value !== UNATTACHED_TOKEN);
  const searchable = sowOnlyOptions.length > SOW_SEARCH_THRESHOLD;
  const visibleSowOptions = searchable
    ? sowOnlyOptions.filter((o) => matchesSowSearch(o.label, sowSearch))
    : sowOnlyOptions;

  // Reopening starts unfiltered — a stale query from the last visit
  // would silently hide options. Render-time prev-value guard (not an
  // effect) per the react-hooks set-state-in-effect rule.
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open) setSowSearch("");
  }

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  const handleToggleOption = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onToggle(next);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={isActive ? `SOW (${selected.length} selected)` : "SOW"}
        className={`${CHIP_BASE} ${isActive ? CHIP_ACTIVE : CHIP_INACTIVE}`}
      >
        <span>SOW</span>
        {isActive && (
          <span className="rounded bg-white px-1 text-[10px] text-brand-600 dark:bg-brand-900 dark:text-brand-300">
            {selected.length}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="filter-popover-sow"
          role="listbox"
          aria-multiselectable="true"
          className={SOW_POPOVER_CLASS}
        >
          {searchable && (
            <input
              type="search"
              className="input mb-2 w-full"
              placeholder="Search SOWs…"
              aria-label="Search SOWs"
              value={sowSearch}
              onChange={(e) => setSowSearch(e.target.value)}
              data-testid="filter-sow-search"
            />
          )}
          <ul className="space-y-1">
            {[...pinnedOptions, ...visibleSowOptions].map((opt) => {
              const id = `filter-sow-${opt.value}`;
              return (
                <li key={opt.value}>
                  <label
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-body-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <input
                      id={id}
                      type="checkbox"
                      checked={selected.includes(opt.value)}
                      onChange={() => handleToggleOption(opt.value)}
                      className="accent-brand-600"
                    />
                    <span>{opt.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
          {searchable && visibleSowOptions.length === 0 && (
            <p
              className="px-2 py-1 text-caption text-slate-500 dark:text-slate-400"
              data-testid="filter-sow-search-empty"
            >
              No SOWs match &ldquo;{sowSearch.trim()}&rdquo;.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

interface BinaryChipProps {
  label: string;
  active: boolean;
  onToggle: () => void;
}

function BinaryChip({ label, active, onToggle }: BinaryChipProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      aria-label={label}
      className={`${CHIP_BASE} ${active ? CHIP_ACTIVE : CHIP_INACTIVE}`}
    >
      {label}
    </button>
  );
}

export default function MilestoneFilterChipBar({
  filters,
  onFiltersChange,
  search,
  onSearchChange,
  sows,
  showSowChip = true,
  resetCounter,
}: MilestoneFilterChipBarProps) {
  const update = <K extends keyof MilestoneFilterChipBarFilters>(
    key: K,
    value: MilestoneFilterChipBarFilters[K],
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const sowOptions = useMemo<OptionDef<string>[]>(() => {
    const opts: OptionDef<string>[] = [
      { value: UNATTACHED_TOKEN, label: "Unattached" },
    ];
    if (!sows) return opts;
    const active = [...sows]
      .filter((s) => !s.archivedAt && s.status !== "cancelled")
      .sort((a, b) => compareSowNumbers(a.number, b.number));
    for (const s of active) {
      opts.push({ value: s.id, label: `${s.number} · ${s.name}` });
    }
    return opts;
  }, [sows]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <DebouncedSearchInput
        value={search}
        onDebouncedChange={onSearchChange}
        resetCounter={resetCounter}
        ariaLabel="Search milestones"
        placeholder="Search milestones…"
      />

      <StatusChip
        selected={filters.status}
        onToggle={(next) => update("status", next)}
      />
      {showSowChip && (
        <SowChip
          options={sowOptions}
          selected={filters.sow}
          onToggle={(next) => update("sow", next)}
        />
      )}
      <BinaryChip
        label="Awaiting acceptance"
        active={filters.awaitingAcceptance}
        onToggle={() => update("awaitingAcceptance", !filters.awaitingAcceptance)}
      />
      <BinaryChip
        label="Include archived"
        active={filters.includeArchived}
        onToggle={() => update("includeArchived", !filters.includeArchived)}
      />
    </div>
  );
}
